import type { ReactNode } from 'react';
import { cn } from './cn';

export interface EmptyStateProps {
  /** Icon (lucide) shown in a soft chip. */
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Primary action(s) — e.g. a "Create" button, or a "Clear filters" link. */
  action?: ReactNode;
  /** Compact variant for inside cards/table bodies. */
  compact?: boolean;
  className?: string;
}

/**
 * One brand empty state (replaces antd `<Empty>`, emoji "🎉", and ad-hoc "No data"
 * text found across the app). Distinguish a genuine empty from a filtered-empty via
 * the copy you pass; never render this while data is still loading (use a skeleton).
 */
export function EmptyState({ icon, title, description, action, compact, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'gap-2 px-4 py-8' : 'gap-3 px-6 py-14',
        className,
      )}
    >
      {icon && (
        <span
          className={cn(
            'grid place-items-center rounded-full bg-surface-2 text-subtle',
            compact ? 'size-10 [&>svg]:size-5' : 'size-14 [&>svg]:size-7',
          )}
        >
          {icon}
        </span>
      )}
      <div className={cn('font-semibold text-content', compact ? 'text-sm' : 'text-base')}>{title}</div>
      {description && (
        <p className={cn('max-w-sm text-muted', compact ? 'text-[13px]' : 'text-sm leading-relaxed')}>
          {description}
        </p>
      )}
      {action && <div className="mt-1 flex flex-wrap items-center justify-center gap-2">{action}</div>}
    </div>
  );
}
